import type { ThemeConfig } from "antd";

/** Design tokens shared by the web ARM and the React Native TSD app. */
export const tokens = {
  color: {
    primary: "#2f6fed",
    primarySoft: "#e8f0ff",
    success: "#1f9d55",
    warning: "#d98c1f",
    danger: "#d64545",
    info: "#3b82f6",
    neutral: "#8c8c8c",
    text: "#111827",
    textSecondary: "#6b7280",
    background: "#f3f5f9",
    surface: "#ffffff",
    border: "#e6eaf0",
  },
  // UXW-07 (ТЗ «Рабочие столы» 31.10.2026): 12 px for buttons and fields, 16 px for cards and dialogs (borderRadiusLG)
  radius: 12,
  // R39 (55+): 15 px text and 40 px controls on the desktop; the terminal keeps its own large sizes
  font: { base: 15, small: 13, large: 18, tsd: 22 },
  space: { xs: 4, sm: 8, md: 16, lg: 24 },
  fontFamily: "Inter, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
} as const;

export const STATUS_COLORS: Record<string, string> = {
  neutral: tokens.color.neutral,
  info: tokens.color.info,
  progress: tokens.color.primary,
  success: tokens.color.success,
  warning: tokens.color.warning,
  danger: tokens.color.danger,
};

// UXW-07: a large button or field keeps the 12 px of the others (the 16 px «LG» radius is for cards, dialogs and
// popups); radio-button groups and «Segmented» keep the shape they had — switches are not rounded further
const FIELD_SHAPE = { borderRadiusLG: tokens.radius } as const;
const SWITCH_SHAPE = { borderRadius: 10, borderRadiusLG: 14 } as const;

/** Ant Design theme: soft surfaces, rounded corners, calm borders, Inter. */
export const antdTheme: ThemeConfig = {
  token: {
    colorPrimary: tokens.color.primary,
    colorSuccess: tokens.color.success,
    colorWarning: tokens.color.warning,
    colorError: tokens.color.danger,
    colorInfo: tokens.color.info,
    colorText: tokens.color.text,
    colorTextSecondary: tokens.color.textSecondary,
    // UX-05: placeholders and disabled text stay readable (≥ 4.5:1 on white)
    colorTextPlaceholder: "#5f6b7a",
    colorTextDisabled: "#596273",
    colorTextTertiary: "#5f6b7a",
    colorTextQuaternary: "#6b7280",
    colorBorder: "#d9dfe7",
    colorBorderSecondary: tokens.color.border,
    colorBgLayout: tokens.color.background,
    borderRadius: tokens.radius,
    borderRadiusLG: 16,
    borderRadiusSM: 8,
    fontSize: tokens.font.base,
    fontFamily: tokens.fontFamily,
    controlHeight: 40,
    controlHeightSM: 30,
    controlHeightLG: 48,
    boxShadowTertiary: "0 1px 2px rgba(16, 24, 40, 0.04), 0 1px 3px rgba(16, 24, 40, 0.06)",
    wireframe: false,
  },
  components: {
    Layout: { headerHeight: 60, headerPadding: "0 24px", siderBg: tokens.color.surface, bodyBg: tokens.color.background },
    Menu: {
      itemHeight: 38,
      itemBorderRadius: 8,
      itemMarginInline: 8,
      subMenuItemBg: "transparent",
      itemSelectedBg: tokens.color.primarySoft,
      itemSelectedColor: tokens.color.primary,
      groupTitleFontSize: 12,
    },
    Card: { paddingLG: 20, headerFontSize: 15, headerHeight: 48, headerHeightSM: 42 },
    Table: { headerBg: "#f7f9fc", headerColor: tokens.color.textSecondary, cellPaddingBlock: 9, rowHoverBg: "#f5f8ff", headerBorderRadius: 10, borderColor: tokens.color.border },
    Button: { fontWeight: 500, primaryShadow: "0 1px 2px rgba(47, 111, 237, 0.25)", ...FIELD_SHAPE },
    Segmented: { itemSelectedBg: tokens.color.surface, trackBg: "#eef1f6", itemSelectedColor: tokens.color.text, ...SWITCH_SHAPE },
    Radio: SWITCH_SHAPE,
    Select: FIELD_SHAPE,
    InputNumber: FIELD_SHAPE,
    DatePicker: FIELD_SHAPE,
    Tabs: { titleFontSize: 14, horizontalMargin: "0 0 16px 0" },
    Tag: { borderRadiusSM: 6 },
    Drawer: { paddingLG: 20 },
    Modal: { titleFontSize: 16 },
    Statistic: { titleFontSize: 13, contentFontSize: 24 },
    Input: { activeShadow: "0 0 0 3px rgba(47, 111, 237, 0.15)", ...FIELD_SHAPE },
  },
};

/** Dark counterpart: same shapes and spacing, surfaces and text taken from the dark palette (`--wms-*` dark vars). */
export const darkTokens = {
  text: "#e5e7eb",
  textSecondary: "#9ca3af",
  background: "#141518",
  surface: "#1b1c20",
  surfaceRaised: "#222328",
  border: "#33363e",
  borderSecondary: "#2a2c33",
  primarySoft: "#1e2a44",
  primaryText: "#8ab4ff",
} as const;

export const antdDarkTheme: ThemeConfig = {
  token: {
    ...antdTheme.token,
    colorText: darkTokens.text,
    colorTextSecondary: darkTokens.textSecondary,
    colorTextTertiary: "#a3abb8",
    colorTextQuaternary: "#8b94a3",
    colorTextPlaceholder: "#a3abb8",
    colorTextDisabled: "#9aa3b2",
    colorBorder: darkTokens.border,
    colorBorderSecondary: darkTokens.borderSecondary,
    colorBgLayout: darkTokens.background,
    colorBgContainer: darkTokens.surface,
    colorBgElevated: darkTokens.surfaceRaised,
    colorBgSpotlight: "#2a2c33",
    colorFillAlter: "#1f2025",
    colorFillSecondary: "#2a2c33",
    colorFillTertiary: "#24262c",
    colorFillQuaternary: "#1f2025",
    boxShadowTertiary: "0 1px 2px rgba(0, 0, 0, 0.4)",
  },
  components: {
    ...antdTheme.components,
    Layout: { headerHeight: 60, headerPadding: "0 24px", siderBg: darkTokens.surface, bodyBg: darkTokens.background, headerBg: darkTokens.surface },
    Menu: {
      itemHeight: 38,
      itemBorderRadius: 8,
      itemMarginInline: 8,
      subMenuItemBg: "transparent",
      itemBg: "transparent",
      itemColor: darkTokens.text,
      itemHoverBg: "#24262c",
      itemSelectedBg: darkTokens.primarySoft,
      itemSelectedColor: darkTokens.primaryText,
      groupTitleFontSize: 12,
    },
    Table: { headerBg: darkTokens.surfaceRaised, headerColor: darkTokens.textSecondary, cellPaddingBlock: 9, rowHoverBg: "#24262c", headerBorderRadius: 10, borderColor: darkTokens.borderSecondary },
    Segmented: { itemSelectedBg: "#2e3139", trackBg: darkTokens.surfaceRaised, itemSelectedColor: darkTokens.text, ...SWITCH_SHAPE },
    Input: { activeShadow: "0 0 0 3px rgba(138, 180, 255, 0.2)", ...FIELD_SHAPE },
    Card: { paddingLG: 20, headerFontSize: 15, headerHeight: 48, headerHeightSM: 42, colorBgContainer: darkTokens.surface },
  },
};
